import { APIResponse, ConfigurableAppQueryArgs, Offer, SuitabilityItem, useAppQuery } from '@alluradirect/react';
import { CheckoutParamEnum } from 'components/views/pages/Checkout/types';
import { usePrevious } from 'hooks/usePrevious';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { PaymentInformation } from 'scenes/dashboard/views/CalendarManagementDialog/types';

const CANCELLATION_POLICY_DATE_RANGE = {
  ID: 6,
  DATE_ACTIVATED: '2021-05-01',
  LABEL: 'Up to 6 Days Before',
  DESCRIPTION: 'No Refund',
  FROM_DAYS: '1',
  TO_DAYS: '6',
  REFUND_PERCENTAGE: '0',
};

type CANCELLATION_POLICY_DATE_RANGE_TYPE = typeof CANCELLATION_POLICY_DATE_RANGE;

export interface BookingCheckoutResponse {
  CREDITS: {
    todo: string;
  };
  BOOKING: {
    PAYMENT_INFORMATION: PaymentInformation;
    OFFERS: {
      ALLURA: {
        OFFER: "" | Offer,
        TOTAL: string;
      },
      OWNER: {
        OFFER: "" | Offer,
        TOTAL: string;
      },
    };
    GIFT_CERTIFICATES: {
      OWNER: {
        OFFER: "" | Offer
      },
      ALLURA: {
        OFFER: "" | Offer,
      },
    };
    TIMELINE: [];
    GUEST_REQUESTS: string;
    SURCHARGES: {
      TAXES_TOTALS: [];
      LIST: {};
      NET: string;
      GROSS: string;
      TAXES_TOTAL: string;
    };
    TAX: {
      RATE_TOTAL: number;
      BOOKING: [
        {
          IS_PASS_THROUGH_TO_OWNER: boolean;
          NAME: string;
          ID: number;
          RATE: number;
          AMOUNT: string;
        },
      ];
      BOOKING_AND_FEES: [
        {
          NAME: string;
          ID: number;
          RATE: number;
          AMOUNT: string;
        },
      ];
      BOOKING_AND_FEES_TOTAL: number;
      TOTAL: string;
      PERCENTAGES: string;
      TOTALS: [
        {
          IS_PASS_THROUGH_TO_OWNER: false;
          NAME: string;
          ID: number;
          RATE: number;
          AMOUNT: string;
        },
      ];
    };
    GUEST_COMMENT: string;
    TOTALS_PAID: {
      REVENUE_NET: string;
      OWNER_PASS_THROUGH_TAXES: [];
      BANK_DEPOSIT: string;
      TAX: string;
      ACCOMMODATION_NET: string;
      ACCOMMODATION_TAXES: [];
      COMMISSION: string;
      CREDIT_CARD_FEE: string;
      SURCHARGE: {
        TAXES: [];
        NET: string;
      };
      COMMISSION_PARTNER: string;
      NR6: string;
      PROPERTY_MANAGER_SERVICE_FEE: {
        TAXES: [
          {
            IS_PASS_THROUGH_TO_OWNER: boolean;
            NAME: string;
            ID: number;
            RATE: number;
            AMOUNT: string;
          },
        ];
        NET: string;
        TOTAL: string;
        IS_FEE_ON_SUBTOTAL_TO_OWNER: boolean;
      };
      DAMAGE_DEPOSIT: string;
    };
    REVIEW: {};
    PROPERTY: {
      DAMAGE_DEPOSIT: {
        MODE: 'Damage Upon Departure';
        DUE_DATE: '';
        MODE_ID: number;
        IS_COLLECTED: boolean;
        AMOUNT: '1000.00';
        DESCRIPTION: 'A refundable Damage Deposit of $123.00 will be charged when Booking Balance is collected';
      };
      IS_ENABLED: number;
      SURCHARGES: {
        PETS: { IS_ENABLED: false; NAME: 'Pets'; ID: 2; AMOUNT: '0.00' };
        CLEANING: { IS_ENABLED: false; NAME: 'Cleaning'; ID: 1; AMOUNT: '0.00' };
      };
      REVIEW_SCORE_AVERAGE: number;
      MAX_GUESTS: number;
      DIRECT_VACATIONS: {
        IS_ACTIVE: true;
        URL: 'https://aspensatwhistler.directvacations.com/';
        LOGO: 'https://images.directvacations.com/DVLogos/default.gif';
        NAME: 'aspensatwhistler';
        ID: 853;
      };
      THUMBNAIL_URL: 'https://images.directvacations.com/Images/adi/property/1794/ADIP1794_30D461EC-E3C9-4FE1-87BB-5E610DCAF4E6_T.jpg?140351733615600';
      IS_PET_FRIENDLY: false;
      ACCESS_TO_UNIT: {
        STANDARD_CODE: '';
        REQUIRES_ENTRY_CODE: true;
        CODE: { NAME: 'Unique Per Guest'; ID: 2 };
        TYPE: { NAME: 'Access Code'; ID: 1 };
        METHOD: { NAME: 'Email'; ID: 2 };
      };
      CODES: {
        FRONT_DOOR_CODE: '';
        DEVELOPMENT_POOL_CODE: '';
        DEVELOPMENT_GYM_CODE: '';
        DEVELOPMENT_HOT_TUB_CODE: '';
        PROPERTY_ID: 1794;
        SKI_LOCKER_CODE: '';
        DEVELOPMENT_DOOR_CODE: '';
        INTERNET_NETWORK_NAME: '';
        BIKE_LOCKER_CODE: '';
        PHONE_NUMBER: '';
        INTERNET_PASSWORD: '';
        DEVELOPMENT_SAUNA_CODE: '';
        DEVELOPMENT_STEAM_ROOM_CODE: '';
        ID: 0;
        GAMES_REC_ROOM_CODE: '';
        GARBAGE_RECYCLING_CODE: '';
        GARAGE_DOOR_CODE: '';
        DATE_LAST_UPDATED: '';
      };
      SQUARE_FEET: 590;
      STYLE: 'Condo';
      JOIN_STATUS: {
        HAS_COMPLETED_ONBOARDING: true;
        STATUS_ID: 7;
        STATUS: 'Listing Active';
        COMPLETION_PERCENTAGE_REQUIRED: 100;
        SUBFORMS_OPTIONAL_COMPLETED: string[];
        DATES: { CREATED: '2009-01-16'; LAST_UPDATED: '2022-08-02'; ACTIVATED: ''; ACTIVATION_REQUEST: '' };
        COMPLETION_PERCENTAGE_OPTIONAL: 100;
        SUBFORMS_COMPLETED: string[];
      };
      URL: 'https://myles.allura.local/1794';
      PARKING: {
        HAS_ADDITIONAL_PAID_SPOTS: true;
        HAS_ADDITIONAL_FREE_SPOTS: false;
        ID: 47;
        NUM_SPOTS_PROVIDED: 0;
        IS_DEFAULT_DESCRIPTION: false;
        DESCRIPTION: 'Parking is secure underground garage for $20/night. Clearance for the garage is 6’6. There is no over height vehicle parking at The Aspens.';
      };
      NAME: 'Aspens - #303';
      ID: 1794;
      CANCELLATION_POLICY: {
        POINTS: [{TYPE: '', LABEL: ''}];
        IS_REFUND_REBOOKED_DATES: true;
        DATE_ACTIVATED: '2021-05-01';
        IS_CREDIT_ONLY_REFUND: false;
        ADMIN_PENALTY: 0;
        IS_SPECIAL_CIRCUMSTANCE: true;
        ADMIN_PENALTY_UNIT: '';
        SPECIAL_CIRCUMSTANCE_DESCRIPTION: 'Covid 2021: Full refund offered if there is a government ordered shut down of Whistler Blackcomb and restaurants. For other covid circumstances, a cancellation fee of $100 may apply.';
        NAME: 'Strict';
        ID: 1742;
        BOOKING_GRACE_PERIOD: '24 Hour Grace period for guest to undo reservation with refund of amount paid with a 3% processing fee deducted';
        SPECIAL_CIRCUMSTANCE_NAME: 'Other/Custom';
        POLICY_ID: 2;
        SURCHARGE_REFUND: 'Cleaning & Pet Fees are refunded for all cancellations';
        DESCRIPTION: "50% refund up until 60 days prior to check in.\nCleaning fees are always refunded if the reservation is canceled before check-in.\nThe alluraDirect service fee is non-refundable. alluraDirect will mediate when necessary, and has the final say in all disputes.\nFor a full refund of the nightly rate, the guest must cancel within 48 hours of booking and at least 14 full days prior to listing's local check-in time (shown in the confirmation email).\nFor a 50% refund of the nightly rate, the guest must cancel 7 full days before the listing's local check in time (shown in the confirmation email), otherwise no refund. If only 50% of the reservation has been paid, no refund will be issued and the remaining 50% will simply not be charged.\nIf the guest cancels less than 60 days in advance, the nights not spent are not refunded.\nIf guest arrives and decides to leave early, the nights not spent are not refunded.\nCancellation policies may be superseded by the Guest Refund Policy, extenuating circumstances, or cancellations by alluraDirect for any other reason permitted under the Terms of Service. Please review these exceptions.\n";
        SERVICE_FEE_REFUND: 'Refundable when host refund is 100%, otherwise non-refundable.';
        DATE_RANGES: CANCELLATION_POLICY_DATE_RANGE_TYPE[];
        SPECIAL_CIRCUMSTANCE_ID: 3;
        DATE_LAST_UPDATED: '2021-05-10';
      };
      GATEWAY_MODE: { ID: number; VALUE: 'open' };
      RULES: {
        ID: 0;
        ADVANCED_BOOKING: { NAME: '1 Day'; ID: 1 };
        CHECK_OUT: '10:00';
        CHECK_IN: '16:00';
        MAX_BOOKABLE_DAYS: 30;
        MAX_GUESTS: 2;
        MIN_AGE_TO_BOOK: 25;
        QUIET_TIME: '22:00';
      };
      SUITABILITIES: {
        LONG_TERM_RENTALS: SuitabilityItem;
        PARTIES_EVENTS: SuitabilityItem;
        PETS: SuitabilityItem;
        SMOKING: SuitabilityItem;
        TRAVEL_RESELLERS: SuitabilityItem;
        WHEELCHAIR_ACCESSIBLE: SuitabilityItem;
      };
      NUM_REVIEWS: 31;
      DESCRIPTION: '2 beds, 1 baths';
      ADDRESS: {
        RESORT: 'Whistler';
        LONGITUDE: -122.94318;
        AREA: '';
        COUNTRY: 'Canada';
        AREA_ID: 0;
        POSTAL: 'v0n 1b4';
        COUNTRY_CODE: 'CAN';
        STREET_NAME: 'Spearhead Drive';
        DEVELOPMENT_ID: 18;
        LATITUDE: 50.11645;
        REGION_ID: 1;
        ID: 1794;
        UNIT: '303';
        REGION: 'British Columbia';
        CITY: 'Whistler';
        STREET_NUMBER: '4800';
        COUNTRY_ID: 1;
        DEVELOPMENT: 'Aspens';
        RESORT_ID: 1;
      };
      SIZE_CATEGORY: '1 Bedroom';
      PRIMARY_CONTACT: {
        IS_EMAIL_VERIFIED: false;
        DATE_SMS_VERIFIED: '';
        DATE_LAST_LOGIN: '2022-08-02';
        COMPANY_NAME: '';
        GUEST_PROFILE: {
          MEMBER_SINCE: '1999-11-08';
          PERMISSIONS: { SHOW_NUM_REVIEWS: true; SHOW_MEMBER_SINCE: true; SHOW_NUM_RECOMMENDS: true };
          NUM_BOOKINGS: 0;
          NUM_RECOMMENDATIONS: 0;
          NUM_REVIEWS: 0;
        };
        DATE_EMAIL_VERIFIED: '';
        DATE_OF_BIRTH: '';
        FIRST_NAME: 'Kerry';
        OWNER_PROFILE: {
          MEMBER_SINCE: '1999-11-08';
          PROPERTY_IDS: [182, 1692, 1769, 1794, 3365, 3440, 4043, 4044, 4045, 4046, 4047, 4048, 4049, 4050];
        };
        IS_SMS_VERIFIED: false;
        THUMBNAIL: 'https://www.gravatar.com/avatar/7c70eb673021ab81c6313f4505647460?d=https%3A%2F%2Fstaging%2Ealluradirect%2Ecom%2F%2Fstyles%2Fimages%2Fhost%2Dprofile%2Epng';
        IS_SUPERUSER: false;
        LAST_NAME: 'Wallace';
        MARKETING_EMAILS: true;
        IS_STAFF: false;
        ID: 178;
        DATE_UPDATED: '2022-07-29';
        PHONE: '16049667100';
        IS_COMPANY: false;
        ADDRESS: {
          RESORT: '';
          LONGITUDE: 0;
          AREA: '';
          COUNTRY: 'Canada';
          AREA_ID: 0;
          POSTAL: 'V8B 0A1';
          COUNTRY_CODE: 'CAN';
          STREET_NAME: '78';
          DEVELOPMENT_ID: 0;
          LATITUDE: 0;
          REGION_ID: 1;
          ID: 178;
          UNIT: '';
          REGION: 'British Columbia';
          CITY: 'Squamish';
          STREET_NUMBER: 'Box';
          COUNTRY_ID: 1;
          DEVELOPMENT: '';
          RESORT_ID: 0;
        };
        EMAIL: 'kerrywallace@telus.net';
      };
    };
    REFERENCE: '220-B-0';
    STATUS: {
      NAME: 'Temporary Booking';
      ID: 0;
    };
    FLAGS: {
      IS_EDITABLE: boolean;
      IS_DIRECT_VACATIONS: boolean;
      IS_PAST_DUE: boolean;
      IS_ALLURA_BOOKING: boolean;
      IS_BLOCK_OFF: boolean;
    };
    CONTACT_TRACING: [];
    STATUSES: [
      {
        DATE: '2022-08-02';
        IS_DONE: boolean;
        LABEL: 'Deposit Due';
      },
      {
        DATE: '2022-08-02';
        IS_DONE: boolean;
        LABEL: 'Balance Due';
      },
      {
        DATE: '2022-08-15';
        IS_DONE: boolean;
        LABEL: 'Arrival Date';
      },
      {
        DATE: '2022-08-22';
        IS_DONE: boolean;
        LABEL: 'Departure Date';
      },
      {
        DATE: '--';
        IS_DONE: boolean;
        LABEL: 'No Review';
      },
    ];
    TOTAL: '4630.90';
    DATES: {
      NEXT_PAYMENT: '2022-08-02';
      ARRIVAL: '2022-08-15';
      CHECK_IN_SENT: '';
      DEPARTURE: '2022-08-22';
      PROPERTY_GUIDEBOOK_SEND: '2022-08-08';
      BOOKING: '2022-08-02';
    };
    PARTY_SIZE: {
      ADULTS: 1;
      KIDS: 0;
      MAX: 8;
    };
    TRANSACTIONS: [];
    CUSTOM_MESSAGE: {
      MESSAGE: '';
      ENTRY_CODE: '';
    };
    BLOCK_OFF_REASON: '';
    INQUIRIES: [];
    SOURCE: 'alluraDirect';
    NIGHTS: 7;
    PURPOSE: '';
    RENTAL_AGREEMENT: string;
    NOTES_OWNER: '';
    SERVICE_FEE: {
      TAX: '38.98';
      TAXES: [
        {
          IS_PASS_THROUGH_TO_OWNER: boolean;
          NAME: 'GST';
          ID: 1;
          RATE: 5;
          AMOUNT: '12.18';
        },
        {
          IS_PASS_THROUGH_TO_OWNER: boolean;
          NAME: 'PST';
          ID: 2;
          RATE: 8;
          AMOUNT: '19.49';
        },
        {
          IS_PASS_THROUGH_TO_OWNER: boolean;
          NAME: 'MRDT';
          ID: 19;
          RATE: 3;
          AMOUNT: '7.31';
        },
      ];
      TAXES_STRING: '5% GST, 8% PST, 3% MRDT';
      FEE: '243.65';
      NAME: 'Booking Service Fee';
      ID: 0;
      TOTAL: '282.63';
      DESCRIPTION: '';
    };
    PROPERTY_OWNER: {
      IS_EMAIL_VERIFIED: boolean;
      DATE_SMS_VERIFIED: '';
      DATE_LAST_LOGIN: '';
      COMPANY_NAME: '';
      GUEST_PROFILE: {
        MEMBER_SINCE: '2000-04-11';
        PERMISSIONS: {
          SHOW_NUM_REVIEWS: boolean;
          SHOW_MEMBER_SINCE: boolean;
          SHOW_NUM_RECOMMENDS: boolean;
        };
        NUM_BOOKINGS: 0;
        NUM_RECOMMENDATIONS: 0;
        NUM_REVIEWS: 0;
      };
      DATE_EMAIL_VERIFIED: '';
      DATE_OF_BIRTH: '';
      FIRST_NAME: 'Larry';
      OWNER_PROFILE: {
        MEMBER_SINCE: '2000-04-11';
        PROPERTY_IDS: [220];
      };
      IS_SMS_VERIFIED: boolean;
      THUMBNAIL: 'https://www.gravatar.com/avatar/1ee24d63bbe607aea1bb848d5846ae81?d=https%3A%2F%2Fstaging%2Ealluradirect%2Ecom%2F%2Fstyles%2Fimages%2Fhost%2Dprofile%2Epng';
      IS_SUPERUSER: boolean;
      LAST_NAME: 'Ryan';
      MARKETING_EMAILS: boolean;
      IS_STAFF: boolean;
      ID: 202;
      DATE_UPDATED: '';
      PHONE: '+13602802500';
      IS_COMPANY: boolean;
      ADDRESS: {
        RESORT: '';
        LONGITUDE: 0;
        AREA: '';
        COUNTRY: 'United States';
        AREA_ID: 0;
        POSTAL: '98592';
        COUNTRY_CODE: 'USA';
        STREET_NAME: 'E Dorothy Court PO Box 444';
        DEVELOPMENT_ID: 0;
        LATITUDE: 0;
        REGION_ID: 61;
        ID: 202;
        UNIT: '';
        REGION: 'Washington';
        CITY: 'Union';
        STREET_NUMBER: '41';
        COUNTRY_ID: 2;
        DEVELOPMENT: '';
        RESORT_ID: 0;
      };
      EMAIL: 'success@hctc.com';
    };
    ID: 0;
    PROPERTY_MANAGER_SERVICE_FEE: {
      TAXES: [
        {
          IS_PASS_THROUGH_TO_OWNER: boolean;
          NAME: 'GST';
          ID: 1;
          RATE: 5;
          AMOUNT: string;
        },
        {
          IS_PASS_THROUGH_TO_OWNER: boolean;
          NAME: 'PST';
          ID: 2;
          RATE: 8;
          AMOUNT: string;
        },
        {
          IS_PASS_THROUGH_TO_OWNER: boolean;
          NAME: 'MRDT';
          ID: 19;
          RATE: 3;
          AMOUNT: string;
        },
      ];
      NET: string;
      TOTAL: string;
      IS_FEE_ON_SUBTOTAL_TO_OWNER: boolean;
    };
    NIGHTLY_RATES: [
      {
        DATE: '2022-08-15';
        DAY: 'Monday';
        BASE_RATE: '595.00';
        RATE: '535.50';
        LOS_DISCOUNT: '0.00';
        OFFER_DISCOUNT: '0.00';
      },
      {
        DATE: '2022-08-16';
        DAY: 'Tuesday';
        BASE_RATE: '595.00';
        RATE: '535.50';
        LOS_DISCOUNT: '0.00';
        OFFER_DISCOUNT: '0.00';
      },
      {
        DATE: '2022-08-17';
        DAY: 'Wednesday';
        BASE_RATE: '595.00';
        RATE: '535.50';
        LOS_DISCOUNT: '0.00';
        OFFER_DISCOUNT: '0.00';
      },
      {
        DATE: '2022-08-18';
        DAY: 'Thursday';
        BASE_RATE: '595.00';
        RATE: '535.50';
        LOS_DISCOUNT: '0.00';
        OFFER_DISCOUNT: '0.00';
      },
      {
        DATE: '2022-08-19';
        DAY: 'Friday';
        BASE_RATE: '595.00';
        RATE: '535.50';
        LOS_DISCOUNT: '0.00';
        OFFER_DISCOUNT: '0.00';
      },
      {
        DATE: '2022-08-20';
        DAY: 'Saturday';
        BASE_RATE: '595.00';
        RATE: '535.50';
        LOS_DISCOUNT: '0.00';
        OFFER_DISCOUNT: '0.00';
      },
      {
        DATE: '2022-08-21';
        DAY: 'Sunday';
        BASE_RATE: '595.00';
        RATE: '535.50';
        LOS_DISCOUNT: '0.00';
        OFFER_DISCOUNT: '0.00';
      },
    ];
    PAYMENT: {
      PAID_TO_DATE: string;
      AMOUNT_OUTSTANDING: '4630.90';
      AMOUNT_REFUNDED: '0.00',
      BOOKING_NET: '3748.50';
      NEXT_PAYMENT: {
        DUE_DATE: '2022-08-02';
        IS_PAST_DUE: boolean;
        NAME: 'Balance Due';
        TOTAL: '4630.90';
      };
      PRICE_PER_NIGHT: '535.50';
      BALANCE: {
        DUE_DATE: '2022-08-02';
        NET: string;
        TOTAL: string;
        PAYMENT_DATE: '';
      };
      DEPOSIT: {
        DUE_DATE: '2022-08-02';
        NET: '3748.50';
        TOTAL: '4630.90';
        PAYMENT_DATE: '';
        PERCENTAGE: 100;
      };
      DAMAGE_DEPOSIT: {
        MODE: 'Damage Upon Departure';
        DUE_DATE: '';
        MODE_ID: number;
        IS_COLLECTED: boolean;
        AMOUNT: '1000.00';
      };
      SINGLE: {
        DUE_DATE: '2022-08-02';
        NET: '3748.50';
        TOTAL: '4630.90';
        PAYMENT_DATE: '';
        PERCENTAGE: 100;
      };
      BOOKING_GROSS: '4630.90';
    };
    GUEST: {
      IS_EMAIL_VERIFIED: boolean;
      DATE_SMS_VERIFIED: '';
      DATE_LAST_LOGIN: '';
      COMPANY_NAME: '';
      GUEST_PROFILE: {
        MEMBER_SINCE: '2022-08-02';
        PERMISSIONS: {
          SHOW_NUM_REVIEWS: boolean;
          SHOW_MEMBER_SINCE: boolean;
          SHOW_NUM_RECOMMENDS: boolean;
        };
        NUM_BOOKINGS: 0;
        NUM_RECOMMENDATIONS: 0;
        NUM_REVIEWS: 0;
      };
      DATE_EMAIL_VERIFIED: '';
      DATE_OF_BIRTH: '';
      FIRST_NAME: '';
      IS_SMS_VERIFIED: boolean;
      THUMBNAIL: '';
      IS_SUPERUSER: boolean;
      LAST_NAME: '';
      MARKETING_EMAILS: boolean;
      IS_STAFF: boolean;
      ID: 0;
      DATE_UPDATED: '';
      PHONE: '';
      IS_COMPANY: boolean;
      ADDRESS: {
        RESORT: '';
        LONGITUDE: 0;
        AREA: '';
        COUNTRY: '';
        AREA_ID: 0;
        POSTAL: '';
        COUNTRY_CODE: '';
        STREET_NAME: '';
        DEVELOPMENT_ID: 0;
        LATITUDE: 0;
        REGION_ID: 0;
        ID: 0;
        UNIT: '';
        REGION: '';
        CITY: '';
        STREET_NUMBER: '';
        COUNTRY_ID: 0;
        DEVELOPMENT: '';
        RESORT_ID: 0;
      };
      EMAIL: '';
    };
  };
}

interface CheckoutEndpointArgs {
  isDirectVacations: boolean;
  guestId: number;
  propertyId: number;
  arrival: string;
  departure: string;
  num_guests: number;
  num_pets: number;
  owner_offer_code?: string;
  owner_offer_id?: string;
  allura_offer_code?: string;
  allura_offer_id?: string;
  owner_gift_certificate_code?: string;
  owner_gift_certificate_id?: string;
  allura_gift_certificate_code?: string;
  allura_gift_certificate_id?: string;
}

export const generateBookingCheckoutEndpoint = (args: CheckoutEndpointArgs) => {
  let checkoutEndpoint = `/RESTAUTH/booking/checkout/?property_id=${args.propertyId}&arrival=${args.arrival}&departure=${args.departure}&num_guests=${args.num_guests}`;
  if (args.num_pets) {
    checkoutEndpoint += `&num_pets=${args.num_pets}`;
  }
  if (args.guestId) {
    checkoutEndpoint += `&guest_id=${args.guestId}`;
  }
  if (args.isDirectVacations) {
    checkoutEndpoint += `&is_direct_vacations=${args.isDirectVacations}`;
  }
  if (args.owner_offer_code && args.owner_offer_id) {
    checkoutEndpoint += `&owner_offer_code=${encodeURIComponent(args.owner_offer_code)}&owner_offer_id=${args.owner_offer_id}`;
  }
  if (args.allura_offer_code && args.allura_offer_id) {
    checkoutEndpoint += `&allura_offer_code=${encodeURIComponent(args.allura_offer_code)}&allura_offer_id=${args.allura_offer_id}`;
  }
  if (args.owner_gift_certificate_code && args.owner_gift_certificate_id) {
    checkoutEndpoint += `&owner_gift_certificate_code=${encodeURIComponent(args.owner_gift_certificate_code)}&owner_gift_certificate_id=${args.owner_gift_certificate_id}`;
  }
  if (args.allura_gift_certificate_code && args.allura_gift_certificate_id) {
    checkoutEndpoint += `&allura_gift_certificate_code=${encodeURIComponent(args.allura_gift_certificate_code)}&allura_gift_certificate_id=${args.allura_gift_certificate_id}`;
  }
  return checkoutEndpoint;
};

export const useCheckout = (args: CheckoutEndpointArgs, rest: ConfigurableAppQueryArgs) => {
  const router = useRouter();

  // TODO: add keepPreviousData
  const query = useAppQuery<APIResponse<BookingCheckoutResponse>>({
    endpoint: generateBookingCheckoutEndpoint(args),
    retry: false,
    ...rest,
  });

  const isInvalidDates = isInvalidDatesCheckoutError(query.error);
  const isConflict = isBookingConflictCheckoutError(query.error);

  // Navigate back to first step to select dates if invalid dates
  const prevIsInvalidDates = usePrevious(isInvalidDates);

  useEffect(() => {
    if (router.isReady && prevIsInvalidDates !== null && !prevIsInvalidDates && (isInvalidDates || isConflict)) {
      const currentStep = Number(router.query[CheckoutParamEnum.step]) || 1;
      if (currentStep === 3) return;

      const nextQuery: Record<string, any> = { ...router.query };
      
      if (!router.pathname.includes('contact')) {
        nextQuery[CheckoutParamEnum.step] = 1;
      }
      
      delete nextQuery[CheckoutParamEnum.startDate];
      delete nextQuery[CheckoutParamEnum.endDate];

      router.replace({
        pathname: router.pathname,
        query: nextQuery,
      });
    }
  }, [isInvalidDates, prevIsInvalidDates, router, isConflict]);

  return { query, isInvalidDates };
};

// Possible error strings
// This is extremely brittle. Perhaps we should create our own
// set of error codes for each of these scenarios.
const noValidSeason = 'No valid season';
const bookingConflict = 'conflict';
const minNights = 'minimum nights requirement';
export function isInvalidDatesCheckoutError(err: Error | undefined): boolean {
  if (!err) return false;

  try {
    const objMessage = JSON.parse(err.message);
    const clientMessage = objMessage?.ERRORS?.CLIENT_MESSAGE;

    if (clientMessage && typeof clientMessage === 'string') {
      if (clientMessage.toLowerCase().includes(minNights.toLowerCase())) {
        return true;
      }

      if (clientMessage.toLowerCase().includes(noValidSeason.toLowerCase())) {
        return true;
      }

      if (clientMessage.toLowerCase().includes(bookingConflict.toLowerCase())) {
        return true;
      }
    }
  } catch (e) {
    return false;
  }

  return false;
}

export function isBookingConflictCheckoutError(err: Error | undefined): boolean {
  if (!err) return false;

  try {
    const objMessage = JSON.parse(err.message);
    const clientMessage = objMessage?.ERRORS?.CLIENT_MESSAGE;

    if (clientMessage && typeof clientMessage === 'string') {
      if (clientMessage.toLowerCase().includes('booking conflict') || clientMessage.toLowerCase().includes('must enter start date after')) {
        return true;
      }
    }
  } catch (e) {
    return false;
  }

  return false;
}
